import { type ReactNode } from 'react'
import {
  ModalDialog,
  ModalDialogActions,
  ModalDialogContent,
  ModalDialogDescription,
  ModalDialogFooter,
  ModalDialogHeader,
  ModalDialogTitle,
} from '@/components/ModalDialog'
import { Button } from '@/components/Button'

export interface ResetPasswordDialogProps {
  isOpen: boolean
  isDismissable: boolean
  onClose: () => void
  children: ReactNode
  testId?: string
}

export function ResetPasswordDialog({
  isOpen,
  isDismissable,
  onClose,
  children,
  testId,
}: ResetPasswordDialogProps) {
  return (
    <ModalDialog
      isOpen={isOpen}
      isDismissable={isDismissable}
      onClose={onClose}
      testId={testId}
    >
      {children}
    </ModalDialog>
  )
}

export interface PasswordResetFormProps {
  formId: string
  isPending: boolean
  children: ReactNode
  onCancel: () => void
  sendButtonTestId?: string
  cancelButtonTestId?: string
}

export function PasswordResetForm({
  formId,
  isPending,
  children,
  onCancel,
  sendButtonTestId,
  cancelButtonTestId,
}: PasswordResetFormProps) {
  return (
    <>
      <ModalDialogHeader>
        <ModalDialogTitle>Reset your password</ModalDialogTitle>
        <ModalDialogDescription>
          Enter the email address you're registered with, and we'll send you a
          link to reset your password.
        </ModalDialogDescription>
      </ModalDialogHeader>

      <ModalDialogContent>{children}</ModalDialogContent>

      <ModalDialogFooter>
        <ModalDialogActions>
          <Button
            type="submit"
            label="Send password reset link"
            form={formId}
            isPending={isPending}
            testId={sendButtonTestId}
          />
          <Button
            label="Not now"
            variant="outline"
            onPress={onCancel}
            isDisabled={isPending}
            testId={cancelButtonTestId}
          />
        </ModalDialogActions>
      </ModalDialogFooter>
    </>
  )
}

export interface PasswordResetSuccessProps {
  email: string
  onClose: () => void
  confirmButtonTestId?: string
}

export function PasswordResetSuccess({
  email,
  onClose,
  confirmButtonTestId,
}: PasswordResetSuccessProps) {
  return (
    <>
      <ModalDialogHeader>
        <ModalDialogTitle>Password reset link sent</ModalDialogTitle>
        <ModalDialogDescription>
          <p>
            We sent a reset password email to{' '}
            <span className="text-body-md">{email}</span>.
          </p>
          <br />
          <p>
            Click the reset password link within the email to set your new
            password.
          </p>
        </ModalDialogDescription>
      </ModalDialogHeader>

      <ModalDialogFooter>
        <ModalDialogActions>
          <Button label="OK" onPress={onClose} testId={confirmButtonTestId} />
        </ModalDialogActions>
      </ModalDialogFooter>
    </>
  )
}
